import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

// 多语言内容
const TRANSLATIONS = {
  zh: {
    badge: 'Core Technology · 核心技术',
    title: '六大核心技术支柱',
    subtitle: 'META66元宇宙生态的技术基石，驱动下一代数字文明的全面跃升',
    viewDetails: '查看核心技术详情',
    bannerText: '六大技术支柱 · 驱动数字文明跃升',
    supportTitle: '六大技术支柱对META的支撑作用',
    supportSubtitle: '技术关联度 · 协同效应 · 未来交汇',
    supportDesc1: 'META66的六大核心技术并非孤立存在，而是构成一个相互依存、协同进化的技术生态系统。6G网络是整个系统的血管，提供实时数据传输；Web5.0协议是骨架，定义数字资产与身份的流通规则；XR技术是感官接口，连接人类与数字世界；数字孪生是镜像引擎，将物理世界映射至虚拟空间；AI智能体是大脑，驱动虚拟世界的智能运转；量子加密是免疫系统，保障整个生态的安全可信。',
    supportDesc2: '六大技术的关联度极高——没有6G，XR的实时渲染无法实现；没有AI，数字孪生无法智能运转；没有量子加密，Web5.0的数字资产将面临安全威胁。它们共同构成元宇宙的"技术六边形"，缺一不可。',
    year2030: '未来10年',
    year2040: '未来20年',
    year2050: '未来30年',
    stage2030: '技术成熟期',
    stage2040: '技术融合期',
    stage2050: '文明重构期',
    stats: [
      { label: '核心专利', value: '2,847+' },
      { label: '研发团队', value: '1,200+' },
      { label: '合作机构', value: '380+' },
      { label: '技术覆盖', value: '全球68国' },
    ],
  },
  en: {
    badge: 'Core Technology',
    title: 'Six Core Technology Pillars',
    subtitle: 'The technical foundation of META66 Metaverse ecosystem, driving the comprehensive leap of next-generation digital civilization',
    viewDetails: 'View Core Tech Details',
    bannerText: 'Six Tech Pillars · Driving Digital Civilization',
    supportTitle: 'Six Tech Pillars Supporting META',
    supportSubtitle: 'Tech Correlation · Synergy · Future Convergence',
    supportDesc1: 'META66\'s six core technologies do not exist in isolation, but form an interdependent, co-evolving technical ecosystem. 6G networks are the vascular system, providing real-time data transmission; Web5.0 protocols are the skeleton, defining rules for digital asset and identity circulation; XR technology is the sensory interface, connecting humans to the digital world; digital twins are the mirror engine, mapping the physical world to virtual space; AI agents are the brain, driving intelligent operation of the virtual world; quantum encryption is the immune system, ensuring the security of the entire ecosystem.',
    supportDesc2: 'The correlation between the six technologies is extremely high—without 6G, XR\'s real-time rendering cannot be achieved; without AI, digital twins cannot operate intelligently; without quantum encryption, Web5.0\'s digital assets face security threats. Together they form the "Tech Hexagon" of the Metaverse, indispensable.',
    year2030: 'Next 10 Years',
    year2040: 'Next 20 Years',
    year2050: 'Next 30 Years',
    stage2030: 'Tech Maturity',
    stage2040: 'Tech Fusion',
    stage2050: 'Civilization Reconstruction',
    stats: [
      { label: 'Core Patents', value: '2,847+' },
      { label: 'R&D Team', value: '1,200+' },
      { label: 'Partners', value: '380+' },
      { label: 'Global Coverage', value: '68 Countries' },
    ],
  }
};

const TECHS_ZH = [
  {
    icon: 'ri-signal-tower-line',
    title: '6G超高速网络',
    desc: '太赫兹频段毫秒级超低延迟，支持百万设备并发接入，为元宇宙沉浸体验提供坚实的网络基础设施支撑',
    tag: '商用中',
    tagColor: '#00FF88',
    splatColor: 'rgba(0,255,136,0.18)',
    img: 'https://public.readdy.ai/ai/img_res/a286655332a400d92af0b9fbd9f6607b.jpg',
    detail: '峰值速率1Tbps，延迟&lt;0.1ms，连接密度10⁷/km²',
  },
  {
    icon: 'ri-global-line',
    title: 'Web5.0去中心化协议',
    desc: '用户完全拥有数据主权，跨平台身份互通，构建真正开放、自由的下一代互联网生态体系',
    tag: '研发中',
    tagColor: '#00FFCC',
    splatColor: 'rgba(0,255,204,0.18)',
    img: 'https://readdy.ai/api/search-image?query=web5%20decentralized%20blockchain%20network%20glowing%20cyan%20nodes%20connections%20digital%20web%20futuristic%20neon%20dark%20background%20technology%20ultra%20detailed%20doodle%20art%20style%20vibrant%20colorful&width=400&height=240&seq=coretech-web5-doodle-001&orientation=landscape',
    detail: '去中心化身份DID，跨链互操作，零知识证明',
  },
  {
    icon: 'ri-glasses-2-line',
    title: 'XR全感官沉浸体验',
    desc: '融合VR/AR/MR与全息投影技术，实现视觉、听觉、触觉、嗅觉四维感官的完全沉浸式体验',
    tag: '量产中',
    tagColor: '#FF6B35',
    splatColor: 'rgba(255,107,53,0.18)',
    img: 'https://readdy.ai/api/search-image?query=VR%20AR%20mixed%20reality%20headset%20holographic%20display%20futuristic%20orange%20neon%20glow%20immersive%20experience%20dark%20background%20sci-fi%20technology%20ultra%20detailed%20doodle%20art%20style%20vibrant%20colorful&width=400&height=240&seq=coretech-xr-doodle-001&orientation=landscape',
    detail: '8K分辨率，120Hz刷新，全身动作捕捉',
  },
  {
    icon: 'ri-map-2-line',
    title: '数字孪生引擎',
    desc: '1:1精准还原物理世界，实时同步现实与虚拟空间数据，赋能智慧城市、工业制造与医疗健康',
    tag: '部署中',
    tagColor: '#FFD700',
    splatColor: 'rgba(255,215,0,0.18)',
    img: 'https://readdy.ai/api/search-image?query=digital%20twin%20city%20mirror%20world%20holographic%20projection%20real%20virtual%20fusion%20yellow%20neon%20dark%20background%20futuristic%20technology%20ultra%20detailed%20doodle%20art%20style%20vibrant%20colorful&width=400&height=240&seq=coretech-twin-doodle-001&orientation=landscape',
    detail: '毫米级精度，实时同步，城市级覆盖',
  },
  {
    icon: 'ri-brain-line',
    title: 'AI智能体系统',
    desc: '具备自主学习与进化能力的AI NPC，打造个性化元宇宙体验，推动人机协作新范式的形成',
    tag: '迭代中',
    tagColor: '#A855F7',
    splatColor: 'rgba(168,85,247,0.18)',
    img: 'https://readdy.ai/api/search-image?query=artificial%20intelligence%20neural%20network%20brain%20glowing%20purple%20connections%20AI%20agent%20digital%20mind%20futuristic%20dark%20background%20sci-fi%20ultra%20detailed%20doodle%20art%20style%20vibrant%20colorful&width=400&height=240&seq=coretech-ai-doodle-001&orientation=landscape',
    detail: 'AGI级推理，万亿参数，多模态感知',
  },
  {
    icon: 'ri-shield-keyhole-line',
    title: '量子加密安全体系',
    desc: '基于量子纠缠原理的不可破解通信加密，为元宇宙数字资产与身份信息提供终极安全保障',
    tag: '前沿研究',
    tagColor: '#FF6B9D',
    splatColor: 'rgba(255,107,157,0.18)',
    img: 'https://readdy.ai/api/search-image?query=quantum%20encryption%20security%20shield%20glowing%20pink%20particles%20quantum%20computing%20dark%20background%20futuristic%20technology%20sci-fi%20digital%20ultra%20detailed%20doodle%20art%20style%20vibrant%20colorful&width=400&height=240&seq=coretech-quantum-doodle-001&orientation=landscape',
    detail: '量子密钥分发QKD，后量子密码学',
  },
];

const TECHS_EN = [
  {
    icon: 'ri-signal-tower-line',
    title: '6G Ultra-Speed Network',
    desc: 'Terahertz-band sub-millisecond latency supporting millions of concurrent devices, providing robust network infrastructure for immersive metaverse experiences',
    tag: 'Live',
    tagColor: '#00FF88',
    splatColor: 'rgba(0,255,136,0.18)',
    img: 'https://readdy.ai/api/search-image?query=6G%20network%20tower%20glowing%20green%20signal%20waves%20futuristic%20technology%20infrastructure%20dark%20background%20sci-fi%20digital%20art%20ultra%20detailed%20artistic%20colorful%20doodle%20style%20vibrant%20neon&width=400&height=240&seq=coretech-6g-doodle-001&orientation=landscape',
    detail: 'Peak 1Tbps · Latency &lt;0.1ms · Density 10⁷/km²',
  },
  {
    icon: 'ri-global-line',
    title: 'Web5.0 Decentralized Protocol',
    desc: 'Full user data sovereignty with cross-platform identity interoperability, building a truly open and permissionless next-generation internet ecosystem',
    tag: 'In Dev',
    tagColor: '#00FFCC',
    splatColor: 'rgba(0,255,204,0.18)',
    img: 'https://readdy.ai/api/search-image?query=web5%20decentralized%20blockchain%20network%20glowing%20cyan%20nodes%20connections%20digital%20web%20futuristic%20neon%20dark%20background%20technology%20ultra%20detailed%20doodle%20art%20style%20vibrant%20colorful&width=400&height=240&seq=coretech-web5-doodle-001&orientation=landscape',
    detail: 'Decentralized Identity DID · Cross-chain · ZKP',
  },
  {
    icon: 'ri-glasses-2-line',
    title: 'XR Full-Sensory Immersion',
    desc: 'Converging VR/AR/MR with holographic projection to deliver complete four-dimensional sensory immersion across vision, hearing, touch and smell',
    tag: 'Mass Prod',
    tagColor: '#FF6B35',
    splatColor: 'rgba(255,107,53,0.18)',
    img: 'https://readdy.ai/api/search-image?query=VR%20AR%20mixed%20reality%20headset%20holographic%20display%20futuristic%20orange%20neon%20glow%20immersive%20experience%20dark%20background%20sci-fi%20technology%20ultra%20detailed%20doodle%20art%20style%20vibrant%20colorful&width=400&height=240&seq=coretech-xr-doodle-001&orientation=landscape',
    detail: '8K/eye · 120Hz refresh · Full-body motion capture',
  },
  {
    icon: 'ri-map-2-line',
    title: 'Digital Twin Engine',
    desc: '1:1 precision replication of the physical world with real-time synchronization, empowering smart cities, industrial manufacturing and healthcare',
    tag: 'Deploying',
    tagColor: '#FFD700',
    splatColor: 'rgba(255,215,0,0.18)',
    img: 'https://readdy.ai/api/search-image?query=digital%20twin%20city%20mirror%20world%20holographic%20projection%20real%20virtual%20fusion%20yellow%20neon%20dark%20background%20futuristic%20technology%20ultra%20detailed%20doodle%20art%20style%20vibrant%20colorful&width=400&height=240&seq=coretech-twin-doodle-001&orientation=landscape',
    detail: 'Millimeter precision · Real-time sync · City-scale',
  },
  {
    icon: 'ri-brain-line',
    title: 'AI Agent System',
    desc: 'Self-learning and evolving AI NPCs delivering personalized metaverse experiences, pioneering new paradigms of human-machine collaboration',
    tag: 'Iterating',
    tagColor: '#A855F7',
    splatColor: 'rgba(168,85,247,0.18)',
    img: 'https://readdy.ai/api/search-image?query=artificial%20intelligence%20neural%20network%20brain%20glowing%20purple%20connections%20AI%20agent%20digital%20mind%20futuristic%20dark%20background%20sci-fi%20ultra%20detailed%20doodle%20art%20style%20vibrant%20colorful&width=400&height=240&seq=coretech-ai-doodle-001&orientation=landscape',
    detail: 'AGI-level reasoning · Trillion params · Multimodal',
  },
  {
    icon: 'ri-shield-keyhole-line',
    title: 'Quantum Encryption Security',
    desc: 'Unbreakable quantum-entanglement-based communication encryption providing ultimate security for metaverse digital assets and identity data',
    tag: 'Research',
    tagColor: '#FF6B9D',
    splatColor: 'rgba(255,107,157,0.18)',
    img: 'https://readdy.ai/api/search-image?query=quantum%20encryption%20security%20shield%20glowing%20pink%20particles%20quantum%20computing%20dark%20background%20futuristic%20technology%20sci-fi%20digital%20ultra%20detailed%20doodle%20art%20style%20vibrant%20colorful&width=400&height=240&seq=coretech-quantum-doodle-001&orientation=landscape',
    detail: 'Quantum Key Distribution QKD · Post-quantum crypto',
  },
];

// Mr. Doodle style SVG decorations
function DoodleDecorations() {
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox="0 0 1440 900"
      preserveAspectRatio="none"
      style={{ opacity: 0.18 }}
    >
      {/* Top wavy line - Mr. Doodle style thick wobbly stroke */}
      <path
        d="M0,60 Q80,30 160,65 Q240,100 320,55 Q400,10 480,60 Q560,110 640,58 Q720,6 800,62 Q880,118 960,60 Q1040,2 1120,58 Q1200,114 1280,62 Q1360,10 1440,55"
        stroke="#00FF88" strokeWidth="3" fill="none" strokeLinecap="round" strokeDasharray="none" opacity="0.5"
      />
      {/* Bottom wavy line */}
      <path
        d="M0,840 Q120,810 240,848 Q360,886 480,838 Q600,790 720,845 Q840,900 960,842 Q1080,784 1200,840 Q1320,896 1440,838"
        stroke="#FFD700" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.4"
      />
      {/* Left side squiggly */}
      <path
        d="M30,150 Q60,180 30,210 Q0,240 30,270 Q60,300 30,330 Q0,360 30,390 Q60,420 30,450"
        stroke="#FF6B9D" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.45"
      />
      {/* Right side squiggly */}
      <path
        d="M1410,200 Q1380,230 1410,260 Q1440,290 1410,320 Q1380,350 1410,380 Q1440,410 1410,440 Q1380,470 1410,500"
        stroke="#00FFCC" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.4"
      />
      {/* Big doodle circle top-left */}
      <circle cx="120" cy="180" r="70" stroke="#00FF88" strokeWidth="2.5" fill="none" strokeDasharray="8,6" opacity="0.35"/>
      <circle cx="120" cy="180" r="50" stroke="#00FF88" strokeWidth="1.5" fill="none" strokeDasharray="4,8" opacity="0.2"/>
      {/* Big doodle circle bottom-right */}
      <circle cx="1320" cy="750" r="90" stroke="#A855F7" strokeWidth="2.5" fill="none" strokeDasharray="10,7" opacity="0.3"/>
      <circle cx="1320" cy="750" r="65" stroke="#A855F7" strokeWidth="1.5" fill="none" strokeDasharray="5,9" opacity="0.18"/>
      {/* Star doodles */}
      <path d="M200,80 L205,65 L210,80 L225,80 L213,90 L218,105 L205,95 L192,105 L197,90 L185,80 Z" stroke="#FFD700" strokeWidth="1.5" fill="none" opacity="0.5"/>
      <path d="M1240,120 L1244,108 L1248,120 L1260,120 L1250,128 L1254,140 L1244,132 L1234,140 L1238,128 L1228,120 Z" stroke="#FF6B35" strokeWidth="1.5" fill="none" opacity="0.45"/>
      <path d="M80,700 L84,688 L88,700 L100,700 L90,708 L94,720 L84,712 L74,720 L78,708 L68,700 Z" stroke="#00FFCC" strokeWidth="1.5" fill="none" opacity="0.4"/>
      {/* Doodle arrows */}
      <path d="M350,820 Q370,800 390,820" stroke="#00FF88" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.4"/>
      <path d="M385,812 L390,820 L382,825" stroke="#00FF88" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.4"/>
      <path d="M1050,80 Q1070,60 1090,80" stroke="#FF6B9D" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.4"/>
      <path d="M1085,72 L1090,80 L1082,85" stroke="#FF6B9D" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.4"/>
      {/* Doodle cross/plus marks */}
      <line x1="700" y1="50" x2="700" y2="70" stroke="#FFD700" strokeWidth="2.5" strokeLinecap="round" opacity="0.5"/>
      <line x1="690" y1="60" x2="710" y2="60" stroke="#FFD700" strokeWidth="2.5" strokeLinecap="round" opacity="0.5"/>
      <line x1="1380" y1="500" x2="1380" y2="520" stroke="#00FF88" strokeWidth="2.5" strokeLinecap="round" opacity="0.4"/>
      <line x1="1370" y1="510" x2="1390" y2="510" stroke="#00FF88" strokeWidth="2.5" strokeLinecap="round" opacity="0.4"/>
      <line x1="60" y1="500" x2="60" y2="520" stroke="#FF6B9D" strokeWidth="2.5" strokeLinecap="round" opacity="0.4"/>
      <line x1="50" y1="510" x2="70" y2="510" stroke="#FF6B9D" strokeWidth="2.5" strokeLinecap="round" opacity="0.4"/>
      {/* Doodle dots cluster */}
      <circle cx="500" cy="40" r="4" fill="#00FFCC" opacity="0.5"/>
      <circle cx="515" cy="35" r="3" fill="#00FFCC" opacity="0.4"/>
      <circle cx="530" cy="42" r="5" fill="#00FFCC" opacity="0.35"/>
      <circle cx="940" cy="860" r="4" fill="#FF6B35" opacity="0.5"/>
      <circle cx="955" cy="855" r="3" fill="#FF6B35" opacity="0.4"/>
      <circle cx="970" cy="862" r="5" fill="#FF6B35" opacity="0.35"/>
      {/* Doodle text labels */}
      <text x="50" y="400" fontSize="22" fill="#00FF88" opacity="0.12" fontFamily="Caveat, cursive" transform="rotate(-90, 50, 400)">CORE TECH</text>
      <text x="1390" y="600" fontSize="20" fill="#FFD700" opacity="0.1" fontFamily="Caveat, cursive" transform="rotate(90, 1390, 600)">META66</text>
      {/* Doodle spiral */}
      <path d="M1100,800 Q1120,780 1140,800 Q1160,820 1140,840 Q1120,860 1100,840 Q1080,820 1100,800" stroke="#A855F7" strokeWidth="1.5" fill="none" opacity="0.3"/>
      {/* Doodle zigzag */}
      <path d="M600,870 L620,850 L640,870 L660,850 L680,870 L700,850 L720,870 L740,850 L760,870" stroke="#00FFCC" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.35"/>
      {/* Doodle heart */}
      <path d="M280,860 Q280,848 290,848 Q300,848 300,860 Q300,848 310,848 Q320,848 320,860 Q320,875 300,888 Q280,875 280,860 Z" stroke="#FF6B9D" strokeWidth="1.5" fill="none" opacity="0.35"/>
    </svg>
  );
}

interface CoreTechProps {
  lang?: 'zh' | 'en';
}

export default function CoreTech({ lang = 'zh' }: CoreTechProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.1 });
  const techs = lang === 'en' ? TECHS_EN : TECHS_ZH;
  const t = TRANSLATIONS[lang];
  const { ref: gridRef, visibleItems } = useStaggerAnimation(techs.length, { threshold: 0.05 });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section id="network" className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'linear-gradient(180deg, #060A0E 0%, #070C10 50%, #060A0E 100%)' }}>
      {/* Sci-fi watermark */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=futuristic%20technology%20circuit%20board%20network%20nodes%20glowing%20lines%20dark%20background%20sci-fi%20blueprint%20ultra%20detailed%208k%20cinematic%20wide&width=1920&height=1080&seq=coretech-watermark-bg-001&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-4"
        />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(0,255,136,0.04) 0%, transparent 60%)' }} />
        {/* Doodle grid */}
        <div className="absolute inset-0 opacity-15" style={{
          backgroundImage: 'linear-gradient(rgba(0,255,136,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,136,0.12) 1px, transparent 1px)',
          backgroundSize: '55px 55px',
        }} />
      </div>

      {/* Mr. Doodle SVG decorations */}
      <DoodleDecorations />

      {/* Ambient glows */}
      <div className="absolute top-10 left-10 w-72 h-72 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(0,255,136,0.07) 0%, transparent 70%)', filter: 'blur(50px)' }} />
      <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.06) 0%, transparent 70%)', filter: 'blur(60px)' }} />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <div>
            <div
              className="inline-flex items-center gap-2 px-4 py-2 mb-6 text-sm font-bold"
              style={{
                background: 'rgba(0,255,136,0.1)',
                border: '1.5px solid rgba(0,255,136,0.4)',
                borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                color: '#00FF88',
                fontFamily: 'Caveat, cursive',
                fontSize: '1rem',
              }}
            >
              <i className="ri-cpu-line" />
              {t.badge}
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              {lang === 'zh' ? '六大核心' : 'Six Core'}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FF88] via-[#FFD700] to-[#FF6B35]"> {lang === 'zh' ? '技术支柱' : 'Tech Pillars'}</span>
            </h2>
          </div>
          <div className="max-w-sm">
            <p className="text-white/40 text-sm leading-relaxed" style={{ fontFamily: 'Caveat, cursive', fontSize: '1rem' }}>
              {t.subtitle}
            </p>
            <Link
              to="/tech"
              className="inline-flex items-center gap-2 px-5 py-2.5 mt-4 rounded-full font-semibold text-sm transition-all duration-300 hover:scale-105 whitespace-nowrap"
              style={{ background: 'rgba(0,255,136,0.12)', border: '1.5px solid rgba(0,255,136,0.4)', color: '#00FF88' }}
            >
              <i className="ri-cpu-line" />
              {t.viewDetails}
              <i className="ri-arrow-right-line" />
            </Link>
            {/* Mr. Doodle inline decoration */}
            <div className="flex items-center gap-3 mt-4">
              {['#00FF88', '#FFD700', '#FF6B35', '#00FFCC', '#A855F7', '#FF6B9D'].map((c, i) => (
                <div
                  key={i}
                  className="w-3 h-3"
                  style={{
                    background: c,
                    borderRadius: i % 2 === 0 ? '50% 40% 60% 45% / 45% 60% 40% 55%' : '40% 60% 45% 55% / 60% 40% 55% 45%',
                    opacity: 0.7,
                    transform: `rotate(${i * 15}deg)`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Doodle image banner */}
        <div
          className="relative w-full rounded-3xl overflow-hidden mb-14"
          style={{
            height: '200px',
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.97)',
            transition: 'opacity 0.9s ease 0.2s, transform 0.9s ease 0.2s',
          }}
        >
          <img
            src="https://readdy.ai/api/search-image?query=futuristic%20technology%20panorama%206G%20network%20AI%20quantum%20computing%20XR%20holographic%20displays%20all%20together%20glowing%20neon%20dark%20background%20ultra%20wide%20cinematic%20doodle%20art%20style%20vibrant%20colorful%208k&width=1440&height=200&seq=coretech-banner-doodle-001&orientation=landscape"
            alt="核心技术全景"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(6,10,14,0.8) 0%, transparent 30%, transparent 70%, rgba(6,10,14,0.8) 100%)' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(6,10,14,0.3) 0%, transparent 40%, rgba(6,10,14,0.6) 100%)' }} />
          {/* Doodle overlay on banner */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1440 200" preserveAspectRatio="none">
            <path d="M0,100 Q180,60 360,100 Q540,140 720,100 Q900,60 1080,100 Q1260,140 1440,100" stroke="#00FF88" strokeWidth="2" fill="none" opacity="0.3" strokeDasharray="8,12"/>
            <circle cx="200" cy="50" r="30" stroke="#FFD700" strokeWidth="1.5" fill="none" opacity="0.3" strokeDasharray="4,6"/>
            <circle cx="1240" cy="150" r="40" stroke="#FF6B9D" strokeWidth="1.5" fill="none" opacity="0.3" strokeDasharray="4,6"/>
            <text x="50" y="120" fontSize="18" fill="#00FFCC" opacity="0.25" fontFamily="Caveat, cursive">6G · Web5.0 · XR · AI · 量子 · 孪生</text>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-white/60 text-xl font-bold tracking-widest" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.4rem', textShadow: '0 0 20px rgba(0,255,136,0.5)' }}>
              {t.bannerText}
            </p>
          </div>
        </div>

        {/* Tech Grid */}
        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {techs.map((tech, idx) => {
            const isHovered = hoveredIdx === idx;
            const isVis = visibleItems[idx];
            return (
              <div
                key={tech.title}
                className="group relative rounded-2xl overflow-hidden border cursor-pointer"
                style={{
                  borderColor: isHovered ? `${tech.tagColor}55` : 'rgba(255,255,255,0.08)',
                  background: isHovered
                    ? `linear-gradient(135deg, ${tech.tagColor}08 0%, rgba(255,255,255,0.01) 100%)`
                    : 'rgba(255,255,255,0.02)',
                  transform: isVis
                    ? isHovered ? 'translateY(-8px) rotate(0.4deg)' : 'translateY(0) rotate(0)'
                    : 'translateY(50px)',
                  opacity: isVis ? 1 : 0,
                  transition: `opacity 0.7s ease ${idx * 100}ms, transform 0.6s cubic-bezier(0.34,1.56,0.64,1) ${idx * 100}ms, border-color 0.3s ease, background 0.3s ease`,
                  borderRadius: '16px 12px 18px 14px / 12px 18px 14px 16px',
                }}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                {/* Top accent bar */}
                <div
                  className="h-1.5 w-full transition-all duration-300"
                  style={{
                    background: `linear-gradient(90deg, ${tech.tagColor}, ${tech.tagColor}40)`,
                    opacity: isHovered ? 1 : 0.4,
                  }}
                />

                {/* Image */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={tech.img}
                    alt={tech.title}
                    className="w-full h-full object-cover object-top group-hover:scale-108 transition-transform duration-700"
                    style={{ transform: isHovered ? 'scale(1.08)' : 'scale(1)', transition: 'transform 0.7s ease' }}
                  />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 30%, rgba(6,10,14,0.95) 100%)' }} />

                  {/* Tag */}
                  <div
                    className="absolute top-3 right-3 px-2.5 py-1 text-xs font-semibold"
                    style={{
                      backgroundColor: `${tech.tagColor}20`,
                      color: tech.tagColor,
                      border: `1px solid ${tech.tagColor}50`,
                      fontFamily: 'Caveat, cursive',
                      fontSize: '0.82rem',
                      borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                    }}
                  >
                    {tech.tag}
                  </div>

                  {/* Mr. Doodle corner decoration on image */}
                  <svg className="absolute top-0 left-0 w-full h-full pointer-events-none" viewBox="0 0 400 240" preserveAspectRatio="none" style={{ opacity: isHovered ? 0.35 : 0.15, transition: 'opacity 0.3s' }}>
                    <path d="M0,20 Q40,5 80,22 Q120,39 160,18 Q200,0 240,20" stroke={tech.tagColor} strokeWidth="1.5" fill="none" strokeDasharray="6,8"/>
                    <circle cx="30" cy="200" r="20" stroke={tech.tagColor} strokeWidth="1.2" fill="none" strokeDasharray="4,6"/>
                    <path d="M350,10 L360,0 L370,10 L360,20 Z" stroke={tech.tagColor} strokeWidth="1.2" fill="none"/>
                    <circle cx="370" cy="220" r="8" stroke={tech.tagColor} strokeWidth="1.2" fill="none"/>
                  </svg>

                  {/* Hover glow splat */}
                  {isHovered && (
                    <div
                      className="absolute top-0 left-0 w-full h-full pointer-events-none"
                      style={{ background: `radial-gradient(ellipse at 30% 30%, ${tech.splatColor} 0%, transparent 60%)` }}
                    />
                  )}
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 flex items-center justify-center transition-all duration-300"
                      style={{
                        backgroundColor: `${tech.tagColor}15`,
                        border: `1.5px solid ${tech.tagColor}35`,
                        borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                        transform: isHovered ? 'rotate(10deg) scale(1.1)' : 'rotate(0) scale(1)',
                      }}
                    >
                      <i className={`${tech.icon} text-lg`} style={{ color: tech.tagColor }} />
                    </div>
                    <h3 className="text-white font-bold text-base">{tech.title}</h3>
                  </div>
                  <p className="text-white/40 text-sm leading-relaxed mb-3">{tech.desc}</p>

                  {/* Detail spec */}
                  <div
                    className="p-2.5 mb-3"
                    style={{
                      background: `${tech.tagColor}06`,
                      border: `1px solid ${tech.tagColor}20`,
                      borderRadius: '8px 6px 10px 8px / 6px 10px 8px 8px',
                    }}
                  >
                    <p className="text-xs" style={{ color: tech.tagColor, fontFamily: 'Caveat, cursive', fontSize: '0.85rem' }}>
                      <i className="ri-information-line mr-1" />
                      <span dangerouslySetInnerHTML={{ __html: tech.detail }} />
                    </p>
                  </div>

                  <div
                    className="flex items-center gap-2 text-xs font-semibold transition-all duration-300"
                    style={{
                      color: tech.tagColor,
                      fontFamily: 'Caveat, cursive',
                      fontSize: '0.9rem',
                      gap: isHovered ? '12px' : '8px',
                    }}
                  >
                    <span>{lang === 'zh' ? '了解详情' : 'Learn More'}</span>
                    <i className="ri-arrow-right-line" />
                  </div>
                </div>

                {/* Hover inner glow */}
                {isHovered && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{ boxShadow: `inset 0 0 50px ${tech.tagColor}08`, borderRadius: '16px 12px 18px 14px / 12px 18px 14px 16px' }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Mr. Doodle mid-section image */}
        <div
          className="relative w-full rounded-3xl overflow-hidden my-14"
          style={{
            height: '180px',
            opacity: statsVisible ? 1 : 0,
            transform: statsVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <img
            src="https://readdy.ai/api/search-image?query=futuristic%20data%20center%20quantum%20computer%20glowing%20neon%20lights%20dark%20background%20sci-fi%20technology%20doodle%20art%20style%20colorful%20vibrant%20ultra%20wide%20panoramic%208k&width=1440&height=180&seq=coretech-mid-doodle-001&orientation=landscape"
            alt="技术数据中心"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(6,10,14,0.9) 0%, rgba(6,10,14,0.3) 50%, rgba(6,10,14,0.9) 100%)' }} />
          {/* Doodle overlay */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1440 180" preserveAspectRatio="none">
            <path d="M0,90 Q240,50 480,90 Q720,130 960,90 Q1200,50 1440,90" stroke="#00FFCC" strokeWidth="2" fill="none" opacity="0.4" strokeDasharray="10,14"/>
            <circle cx="100" cy="40" r="25" stroke="#FFD700" strokeWidth="1.5" fill="none" opacity="0.35" strokeDasharray="4,6"/>
            <circle cx="1340" cy="140" r="30" stroke="#FF6B9D" strokeWidth="1.5" fill="none" opacity="0.35" strokeDasharray="4,6"/>
            <path d="M680,20 L690,5 L700,20 L715,20 L703,30 L708,45 L690,35 L672,45 L677,30 L665,20 Z" stroke="#00FF88" strokeWidth="1.5" fill="none" opacity="0.4"/>
          </svg>
        </div>

        {/* ===== 整体说明：六大技术对META的支撑作用 ===== */}
        <div
          className="my-14 p-8 md:p-10 rounded-3xl relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(0,255,136,0.05), rgba(255,215,0,0.05), rgba(168,85,247,0.05))',
            border: '2px solid rgba(0,255,136,0.18)',
            borderRadius: '28px 20px 32px 24px / 20px 32px 24px 28px',
            opacity: statsVisible ? 1 : 0,
            transform: statsVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 pointer-events-none opacity-5" style={{ background: 'radial-gradient(ellipse, rgba(0,255,136,0.8) 0%, transparent 70%)' }} />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center" style={{ background: 'rgba(0,255,136,0.15)', border: '2px solid rgba(0,255,136,0.4)', borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
                <i className="ri-links-line text-xl text-[#00FF88]" />
              </div>
              <div>
                <h3 className="text-white font-black text-xl" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {lang === 'en' ? <>Six Tech Pillars <span className="text-[#00FF88]">Supporting META</span></> : <>六大技术支柱 <span className="text-[#00FF88]">对META的支撑作用</span></>}
                </h3>
                <p className="text-white/35 text-sm" style={{ fontFamily: 'Caveat, cursive' }}>{lang === 'en' ? 'Tech Correlation · Synergy · Future Convergence' : '技术关联度 · 协同效应 · 未来交汇'}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                {lang === 'zh' ? (
                  <>
                    <p className="text-white/65 text-sm leading-relaxed mb-4">
                      META66的六大核心技术并非孤立存在，而是构成一个相互依存、协同进化的技术生态系统。
                      <span className="text-[#00FF88] font-semibold">6G网络</span>是整个系统的血管，提供实时数据传输；
                      <span className="text-[#FFD700] font-semibold">Web5.0协议</span>是骨架，定义数字资产与身份的流通规则；
                      <span className="text-[#FF6B35] font-semibold">XR技术</span>是感官接口，连接人类与数字世界；
                      <span className="text-[#FFD700] font-semibold">数字孪生</span>是镜像引擎，将物理世界映射至虚拟空间；
                      <span className="text-[#A855F7] font-semibold">AI智能体</span>是大脑，驱动虚拟世界的智能运转；
                      <span className="text-[#FF6B9D] font-semibold">量子加密</span>是免疫系统，保障整个生态的安全可信。
                    </p>
                    <p className="text-white/50 text-sm leading-relaxed">
                      六大技术的关联度极高——没有6G，XR的实时渲染无法实现；没有AI，数字孪生无法智能运转；没有量子加密，Web5.0的数字资产将面临安全威胁。它们共同构成元宇宙的"技术六边形"，缺一不可。
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-white/65 text-sm leading-relaxed mb-4">
                      META66's six core technologies do not operate in isolation — they form a deeply interdependent, co-evolving technical ecosystem.
                      <span className="text-[#00FF88] font-semibold"> 6G Networks</span> serve as the vascular system, enabling real-time data transmission at planetary scale.
                      <span className="text-[#FFD700] font-semibold"> Web5.0 Protocols</span> form the skeletal framework, governing the circulation of digital assets and sovereign identities.
                      <span className="text-[#FF6B35] font-semibold"> XR Technology</span> is the sensory interface, bridging human perception and the digital world.
                      <span className="text-[#FFD700] font-semibold"> Digital Twins</span> are the mirror engine, mapping the physical world into virtual space with millimeter precision.
                      <span className="text-[#A855F7] font-semibold"> AI Agents</span> are the cognitive core, powering intelligent operation across the virtual realm.
                      <span className="text-[#FF6B9D] font-semibold"> Quantum Encryption</span> is the immune system, guaranteeing the security and trustworthiness of the entire ecosystem.
                    </p>
                    <p className="text-white/50 text-sm leading-relaxed">
                      The interdependency is absolute — without 6G, XR real-time rendering is impossible; without AI, digital twins cannot operate intelligently; without quantum encryption, Web5.0 digital assets face existential security threats. Together, they form the Metaverse's "Technology Hexagon" — remove any one pillar, and the entire structure collapses.
                    </p>
                  </>
                )}
              </div>
              <div className="flex flex-col gap-3">
                {(lang === 'en' ? [
                  {
                    year: '2030', label: 'Next 10 Yrs', title: 'Tech Maturity',
                    desc: '6G reaches global commercialization. XR devices slim to everyday eyewear. AI models generate virtual worlds in real time. Metaverse DAU surpasses 1 billion. Virtual offices become the new standard.',
                    color: '#00FF88',
                    img: 'https://readdy.ai/api/search-image?query=year%202030%20futuristic%20smart%20city%20holographic%20displays%20flying%20vehicles%206G%20network%20towers%20glowing%20green%20neon%20people%20wearing%20XR%20headsets%20digital%20twin%20city%20overlay%20dark%20atmospheric%20sci-fi%20ultra%20detailed%20cinematic%208k&width=600&height=200&seq=future2030-v2&orientation=landscape',
                    tags: ['6G Global Rollout', 'XR Ubiquity', 'AI World Gen', 'Digital Twin Cities'],
                  },
                  {
                    year: '2040', label: 'Next 20 Yrs', title: 'Tech Fusion',
                    desc: 'Quantum computers enter commercial deployment. Non-invasive BCIs achieve mass adoption. Web5.0 becomes the global internet standard. Humans navigate the Metaverse through pure thought.',
                    color: '#FFD700',
                    img: 'https://readdy.ai/api/search-image?query=year%202040%20quantum%20computing%20brain%20computer%20interface%20neural%20link%20human%20mind%20digital%20world%20fusion%20golden%20yellow%20neon%20consciousness%20upload%20metaverse%20immersive%20reality%20dark%20sci-fi%20cinematic%20ultra%20detailed%208k&width=600&height=200&seq=future2040-v2&orientation=landscape',
                    tags: ['Quantum Computing', 'BCI Mass Adoption', 'Web5.0 Standard', 'Thought Navigation'],
                  },
                  {
                    year: '2050', label: 'Next 30 Yrs', title: 'Civilization Rebuilt',
                    desc: 'All six technologies fully converge. The virtual-physical boundary dissolves. Human consciousness becomes partially digitizable. The Metaverse becomes humanity\'s second home.',
                    color: '#A855F7',
                    img: 'https://readdy.ai/api/search-image?query=year%202050%20post-singularity%20civilization%20consciousness%20upload%20digital%20immortality%20interstellar%20metaverse%20purple%20violet%20neon%20humans%20and%20AI%20merged%20virtual%20physical%20reality%20dissolved%20cosmic%20scale%20sci-fi%20ultra%20detailed%20cinematic%208k&width=600&height=200&seq=future2050-v2&orientation=landscape',
                    tags: ['Consciousness Upload', 'Full Fusion', 'Interstellar Meta', 'Civilization Rebuilt'],
                  },
                ] : [
                  {
                    year: '2030', label: '未来10年', title: '技术成熟期',
                    desc: '6G全球商用，XR设备轻量化，AI大模型实时生成虚拟世界，数字孪生城市全面落地。元宇宙日活用户突破10亿，虚拟办公成为主流。',
                    color: '#00FF88',
                    img: 'https://readdy.ai/api/search-image?query=year%202030%20futuristic%20smart%20city%20holographic%20displays%20flying%20vehicles%206G%20network%20towers%20glowing%20green%20neon%20people%20wearing%20XR%20headsets%20digital%20twin%20city%20overlay%20dark%20atmospheric%20sci-fi%20ultra%20detailed%20cinematic%208k&width=600&height=200&seq=future2030-v2&orientation=landscape',
                    tags: ['6G全球商用', 'XR普及', 'AI生成世界', '数字孪生城市'],
                  },
                  {
                    year: '2040', label: '未来20年', title: '技术融合期',
                    desc: '量子计算机商用化，脑机接口非侵入式普及，Web5.0成为全球互联网标准。人类可以"意念上网"，虚拟经济规模超越实体经济。',
                    color: '#FFD700',
                    img: 'https://readdy.ai/api/search-image?query=year%202040%20quantum%20computing%20brain%20computer%20interface%20neural%20link%20human%20mind%20digital%20world%20fusion%20golden%20yellow%20neon%20consciousness%20upload%20metaverse%20immersive%20reality%20dark%20sci-fi%20cinematic%20ultra%20detailed%208k&width=600&height=200&seq=future2040-v2&orientation=landscape',
                    tags: ['量子计算商用', '脑机接口普及', 'Web5.0标准', '意念上网'],
                  },
                  {
                    year: '2050', label: '未来30年', title: '文明重构期',
                    desc: '六大技术完全融合，虚实边界消融，人类意识可被部分数字化。元宇宙成为人类文明的第二栖息地，星际元宇宙开始构建。',
                    color: '#A855F7',
                    img: 'https://readdy.ai/api/search-image?query=year%202050%20post-singularity%20civilization%20consciousness%20upload%20digital%20immortality%20interstellar%20metaverse%20purple%20violet%20neon%20humans%20and%20AI%20merged%20virtual%20physical%20reality%20dissolved%20cosmic%20scale%20sci-fi%20ultra%20detailed%20cinematic%208k&width=600&height=200&seq=future2050-v2&orientation=landscape',
                    tags: ['意识数字化', '虚实融合', '星际元宇宙', '文明重构'],
                  },
                ]).map((item) => (
                  <div
                    key={item.year}
                    className="rounded-xl overflow-hidden"
                    style={{
                      border: `1px solid ${item.color}25`,
                      background: `${item.color}04`,
                    }}
                  >
                    {/* Image */}
                    <div className="relative h-28 overflow-hidden">
                      <img src={item.img} alt={item.title} className="w-full h-full object-cover object-center" />
                      <div className="absolute inset-0" style={{ background: `linear-gradient(to right, rgba(6,10,14,0.85) 0%, rgba(6,10,14,0.3) 60%, rgba(6,10,14,0.7) 100%)` }} />
                      <div className="absolute inset-0 flex items-center px-4 gap-3">
                        <div className="flex-shrink-0">
                          <div className="px-2.5 py-1 text-xs font-black rounded-lg" style={{ background: `${item.color}20`, color: item.color, border: `1px solid ${item.color}50`, fontFamily: 'Orbitron, sans-serif', fontSize: '0.85rem', boxShadow: `0 0 12px ${item.color}30` }}>
                            {item.year}
                          </div>
                        </div>
                        <div>
                          <p className="text-white font-black text-sm" style={{ fontFamily: 'Orbitron, sans-serif' }}>{item.title}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {item.tags.map(tag => (
                              <span key={tag} className="text-xs px-1.5 py-0.5 rounded" style={{ background: `${item.color}15`, color: item.color, fontSize: '0.6rem' }}>{tag}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Text */}
                    <div className="px-4 py-3">
                      <p className="text-white/50 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom stats row */}
        <div
          ref={statsRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {[
            { label: '核心专利', value: '2,847+', color: '#00FF88', icon: 'ri-award-line' },
            { label: '研发团队', value: '1,200+', color: '#FFD700', icon: 'ri-team-line' },
            { label: '合作机构', value: '380+', color: '#FF6B35', icon: 'ri-building-line' },
            { label: '技术覆盖', value: '全球68国', color: '#A855F7', icon: 'ri-earth-line' },
          ].map((stat, i) => (
            <div
              key={stat.label}
              className="flex items-center gap-4 p-4 rounded-2xl"
              style={{
                border: `1.5px solid ${stat.color}20`,
                background: `${stat.color}05`,
                borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px',
                opacity: statsVisible ? 1 : 0,
                transform: statsVisible ? 'translateY(0)' : 'translateY(30px)',
                transition: `opacity 0.7s ease ${i * 120}ms, transform 0.7s ease ${i * 120}ms`,
              }}
            >
              <div
                className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: `${stat.color}15`,
                  border: `1.5px solid ${stat.color}35`,
                  borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                }}
              >
                <i className={`${stat.icon} text-lg`} style={{ color: stat.color }} />
              </div>
              <div>
                <p className="font-black text-lg" style={{ color: stat.color, fontFamily: 'Orbitron, sans-serif' }}>{stat.value}</p>
                <p className="text-white/35 text-xs">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
